import React from "react";
import { Box, Flex, HStack, Text, Icon } from "@chakra-ui/react";
import { HiArrowUpRight, HiArrowDownRight } from "react-icons/hi2";
import { c } from "../classroomColors";
import { brand, onTint } from "../standeTokens";
import { chartColors } from "../marking/markingTokens";

const DAY = 86400000;

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * This week's flow, small enough to sit at the foot of the triage card: how
 * much came in, how that compares with last week, and the fourteen-day shape
 * with the busiest day picked out.
 */
const WeekPulse = ({ rows, isDark }) => {
  const cc = chartColors(isDark);
  const b = brand(isDark);
  const tint = onTint(isDark);

  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const submittedAt = (r) =>
    r.submission_status === "SUBMITTED" && r.updated_date_time ? new Date(r.updated_date_time) : null;

  const all = (rows || []).map(submittedAt).filter((d) => d && !Number.isNaN(d.getTime()));

  const weekStart = new Date(startOfToday.getTime() - 6 * DAY);
  const prevStart = new Date(startOfToday.getTime() - 13 * DAY);

  const thisWeek = all.filter((d) => d >= weekStart).length;
  const lastWeek = all.filter((d) => d >= prevStart && d < weekStart).length;
  const delta = thisWeek - lastWeek;

  // 14 days of shape, small and unobtrusive.
  const strip = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(startOfToday.getTime() - (13 - i) * DAY);
    return { d, n: all.filter((x) => sameDay(x, d)).length };
  });
  const stripMax = Math.max(1, ...strip.map((s) => s.n));

  return (
    <Box>
      <Flex align="baseline" gap="8px" wrap="wrap" mb="7px">
        <Text fontSize="19px" fontWeight="500" lineHeight="1" color={c.text}
          sx={{ fontVariantNumeric: "tabular-nums" }}>
          {thisWeek}
        </Text>
        <Text fontSize="12.5px" fontWeight="300" color={c.textSub}>
          in this week
        </Text>
        {(lastWeek > 0 || thisWeek > 0) && (
          <HStack
            spacing="2px" px="6px" py="1px" borderRadius="full"
            bg={delta >= 0 ? b.lightGreen : b.lightYellow}
            color={delta >= 0 ? tint.lightGreen : tint.lightYellow}
          >
            <Icon as={delta >= 0 ? HiArrowUpRight : HiArrowDownRight} boxSize="10px" />
            <Text fontSize="10.5px" fontWeight="500" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {Math.abs(delta)}
            </Text>
          </HStack>
        )}
      </Flex>

      {/* Fourteen days of shape, kept small: the point is the trend, and the
          busiest day is picked out so a deadline spike is visible. */}
      <Flex align="flex-end" gap="3px" height="26px" mb="5px">
        {strip.map((s, i) => {
          const peak = s.n > 0 && s.n === stripMax;
          return (
            <Box
              key={i}
              flex="1"
              height={`${Math.max(s.n ? 3 : 2, (s.n / stripMax) * 24)}px`}
              borderRadius="2px"
              bg={s.n ? (peak ? cc.marked : c.textMuted) : c.track}
              opacity={s.n && !peak ? 0.5 : 1}
              title={`${s.n} on ${s.d.getDate()}/${s.d.getMonth() + 1}`}
            />
          );
        })}
      </Flex>
      <Text fontSize="10.5px" color={c.textMuted}>
        Last 14 days
      </Text>
    </Box>
  );
};

export default WeekPulse;
